import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { useAppSelector } from "app/hooks";
import { store } from "app/store";
import { CURRENT_APPOINTMENT_MERGE_TAG } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import {
  useGetMergeTagsAppointmentQuery,
  useGetMergeTagsCurrentReportQuery,
  useGetMergeTagsPatientProductsQuery,
  useGetMergeTagsPatientQuery,
  useGetMergeTagsReportQuery,
  useGetMergeTagsSaleByIdQuery,
  useGetMergeTagsSaleQuery
} from "services/merge-tags";
import type { LineItem } from "services/types/sales.type";
import { convertHtmlToText, urlToBase64 } from "utils/common";

import { getHtmlLikeText } from "../word-processor-value.utils";
import { selectDataParamsById, setDataParams } from "../word-processor.slice";
import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "./merge-tags-constants";
import { MergeTagsDataSource } from "./merge-tags-data-source.type";
import { PATIENT_SELECTED_INVOICE_OPTIONS } from "./merge-tags-options/patient-selected-invoice.utils";
import type {
  MergeTagExtraData,
  MergeTagExtraDataKeys
} from "./merge-tags-values/merge-tags-values";
import { buildMergeTagValue } from "./merge-tags.utils";
import { isWildcardTag } from "./replace-wildcards.utils";

type useMergeTagsProps = {
  fieldIdRef?: React.MutableRefObject<string | undefined>;
  allowMergeTags?: boolean;
  allowInsertMergeTagsValues?: boolean;
  allowHtml?: boolean;
  maxLength?: number;
  patientId?: number;
  saleId?: number;
  patientSignatureURLRef?: React.MutableRefObject<string | undefined>;
  additionalMergeTagsValues?: MergeTagExtraData;
  allowLeadsContext?: boolean;
};

// IMPORTANT: If a new LineItem field affects merge tag output, add it here.
// Missing fields will silently prevent data source rebuilds on that field's changes.
const generateSaleKey = (items: LineItem[] | undefined) => {
  return (
    items
      ?.map(
        (item) =>
          `${item.id}:${item.inventory_product?.id ?? ""}:${item.inventory_product?.serial_number ?? ""}:${item.inventory_product?.manufacturer_warranty_expiration_date ?? ""}:${item.inventory_product?.loss_damage_warranty_expiration_date ?? ""}:${item.inventory_product?.ear ?? ""}:${JSON.stringify(item.inventory_product?.specification ?? "")}:${item.total ?? ""}:${item?.patient_responsibility ?? ""}:${item.delivered_at ?? ""}:${item?.insurer_responsibility ?? ""}:${item.quantity ?? ""}:${item.amount_paid ?? ""}:${item.returned_at ?? ""}:${item.exchanged_at ?? ""}:${item.tax_amount ?? ""}:${item.adjusted ?? ""}:${item.price ?? ""}:${item.discount ?? ""}:${item?.product?.id ?? ""}:${item.description ?? ""}:${item.taxable ?? ""}:${item.tax_rate ?? ""}:${item.cpt_code?.code ?? ""}:${item.product?.master_product?.type ?? ""}:${item.product?.master_product?.subtype ?? ""}:${item.product?.master_product?.manufacturer?.name ?? ""}:${item.product?.master_product?.model ?? ""}`
      )
      .join(",") ?? ""
  );
};

export default function useMergeTags({
  fieldIdRef,
  allowMergeTags,
  allowInsertMergeTagsValues,
  allowHtml,
  maxLength,
  patientId: initialPatientId,
  saleId: initialSaleId,
  patientSignatureURLRef,
  additionalMergeTagsValues,
  allowLeadsContext
}: useMergeTagsProps) {
  const {
    patientId: paramPatientId,
    saleId: paramSaleId,
    saleConfiguration
  } = useAppSelector((state: any) =>
    selectDataParamsById(state, String(fieldIdRef?.current || ""))
  ) || {};

  const patientId = initialPatientId ?? paramPatientId;
  const saleId = paramSaleId ?? initialSaleId;

  const [doNotFetchMergeTagsDataSources, setDoNotFetchMergeTagsDataSources] =
    useState(true);

  const { currentData: patient, isLoading: isPatientLoading } =
    useGetMergeTagsPatientQuery(
      { patientId: Number(patientId) },
      {
        skip:
          !allowMergeTags ||
          !allowInsertMergeTagsValues ||
          patientId === -1 ||
          !patientId
      }
    );

  useEffect(() => {
    if (patient) {
      setDoNotFetchMergeTagsDataSources(false);
    }
  }, [patient]);

  const {
    data: lastReport,
    isLoading: isLastReportLoading,
    isFetching: isLastReportFetching
  } = useGetMergeTagsReportQuery(Number(patient?.most_recent_report?.id), {
    skip:
      doNotFetchMergeTagsDataSources ||
      !allowMergeTags ||
      !allowInsertMergeTagsValues ||
      !patientId ||
      !patient ||
      !patient?.most_recent_report?.id
  });

  const {
    data: currentReport,
    isLoading: isCurrentReportLoading,
    isFetching: isCurrentReportFetching
  } = useGetMergeTagsCurrentReportQuery(Number(patientId), {
    skip:
      doNotFetchMergeTagsDataSources ||
      !allowMergeTags ||
      !allowInsertMergeTagsValues ||
      !patientId ||
      !patient
  });

  const {
    data: lastAppointmentSale,
    isLoading: isLastAppointmentSalesLoading,
    isFetching: isLastAppointmentSalesFetching
  } = useGetMergeTagsSaleQuery(
    {
      appointmentId: Number(patient?.previous_appointment?.id),
      patientId: Number(patientId)
    },
    {
      skip:
        doNotFetchMergeTagsDataSources ||
        !allowMergeTags ||
        !allowInsertMergeTagsValues ||
        !patientId ||
        !patient?.previous_appointment
    }
  );

  const isContextSaleNotAvailable =
    !saleId ||
    doNotFetchMergeTagsDataSources ||
    !allowMergeTags ||
    !allowInsertMergeTagsValues;

  const {
    data: fetchedContextSale,
    isLoading: isContextSaleLoading,
    isFetching: isContextSaleFetching,
    isUninitialized: isContextSaleUninitialized,
    refetch: refetchContextSale
  } = useGetMergeTagsSaleByIdQuery(
    {
      id: Number(saleId)
    },
    {
      skip: isContextSaleNotAvailable
    }
  );

  const refetchSale = useCallback(() => {
    if (saleId && !isContextSaleUninitialized) {
      refetchContextSale();
    }
  }, [isContextSaleUninitialized, refetchContextSale, saleId]);

  const contextSale = useMemo(() => {
    if (isContextSaleNotAvailable) {
      return undefined;
    }
    return fetchedContextSale;
  }, [fetchedContextSale, isContextSaleNotAvailable]);

  // Stable key that changes only when items are added/removed or a product data changed.
  // Used as a dep instead of the full contextSale object to avoid data source rebuilds on
  // every RTK Query re-render of the same unchanged sale.
  const contextSaleItemsKey = useMemo(
    () => generateSaleKey(contextSale?.items),
    [contextSale?.items]
  );

  const currentClinic = useAppSelector(selectCurrentClinic);

  const flags = useFlags();
  const isCurrentAppointmentMergeTagEnabled = flags[CURRENT_APPOINTMENT_MERGE_TAG] ?? false;

  const {
    data: patientProducts = [],
    isLoading: isPatientProductsLoading,
    isFetching: isPatientProductsFetching
  } = useGetMergeTagsPatientProductsQuery(
    {
      patientId: Number(patientId)
    },
    {
      skip:
        doNotFetchMergeTagsDataSources ||
        !allowMergeTags ||
        !allowInsertMergeTagsValues ||
        !patientId ||
        !patient
    }
  );

  const {
    data: nextAppointment,
    isLoading: isNextAppointmentLoading,
    isFetching: isNextAppointmentFetching
  } = useGetMergeTagsAppointmentQuery(Number(patient?.next_appointment?.id), {
    skip:
      doNotFetchMergeTagsDataSources ||
      !allowMergeTags ||
      !allowInsertMergeTagsValues ||
      !patient ||
      !patient?.next_appointment?.id
  });

  const {
    data: lastAppointment,
    isLoading: isLastAppointmentLoading,
    isFetching: isLastAppointmentFetching
  } = useGetMergeTagsAppointmentQuery(
    Number(patient?.previous_appointment?.id),
    {
      skip:
        doNotFetchMergeTagsDataSources ||
        !allowMergeTags ||
        !allowInsertMergeTagsValues ||
        !patient ||
        !patient?.previous_appointment?.id
    }
  );

  const {
    data: followingAppointment,
    isLoading: isFollowingAppointmentLoading,
    isFetching: isFollowingAppointmentFetching
  } = useGetMergeTagsAppointmentQuery(
    Number(patient?.following_appointment?.id),
    {
      skip:
        doNotFetchMergeTagsDataSources ||
        !allowMergeTags ||
        !allowInsertMergeTagsValues ||
        !patient ||
        !patient?.following_appointment?.id
    }
  );

  const nextAppointmentIsToday = useMemo(() => {
    if (!patient?.next_appointment_moment || !patient?.next_appointment?.clinic?.timezone)
      return false;
    const now = new Date();
    const clinicTz = patient.next_appointment.clinic.timezone;
    const clinicToday = new Date(now.toLocaleString("en-US", { timeZone: clinicTz }));
    const apptMoment = new Date(patient.next_appointment_moment);
    const apptInClinicTz = new Date(apptMoment.toLocaleString("en-US", { timeZone: clinicTz }));
    return (
      clinicToday.getFullYear() === apptInClinicTz.getFullYear() &&
      clinicToday.getMonth() === apptInClinicTz.getMonth() &&
      clinicToday.getDate() === apptInClinicTz.getDate()
    );
  }, [patient?.next_appointment_moment, patient?.next_appointment?.clinic?.timezone]);

  const shouldSplitAppointments = isCurrentAppointmentMergeTagEnabled && nextAppointmentIsToday;

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const shouldSkipQuery =
      !allowMergeTags ||
      !allowInsertMergeTagsValues ||
      patientId === -1 ||
      !patientId;

    if (shouldSkipQuery) {
      setIsReady(true);
      return;
    }

    if (isPatientLoading || !patient) {
      setIsReady(false);
      setIsDataLoaded(false);
      isReadyRef.current = false;
      return;
    }

    const isLastAppointmentSales =
      !isLastAppointmentSalesLoading && !isLastAppointmentSalesFetching;

    const isLastReport =
      Boolean(!patient?.most_recent_report) ||
      (!isLastReportLoading &&
        !isLastReportFetching &&
        Boolean(patient?.most_recent_report?.id));

    const isCurrentReport = !isCurrentReportLoading && !isCurrentReportFetching;

    const isPatientProducts =
      !isPatientProductsLoading && !isPatientProductsFetching;

    const isNextAppointment =
      !Boolean(patient?.next_appointment) ||
      (!isNextAppointmentLoading &&
        !isNextAppointmentFetching &&
        Boolean(patient?.next_appointment));

    const isLastAppointment =
      !Boolean(patient?.previous_appointment) ||
      (!isLastAppointmentLoading &&
        !isLastAppointmentFetching &&
        Boolean(patient?.previous_appointment));

    const isFollowingAppointment =
      !Boolean(patient?.following_appointment) ||
      (!isFollowingAppointmentLoading &&
        !isFollowingAppointmentFetching &&
        Boolean(patient?.following_appointment));

    const isContextSale = isContextSaleNotAvailable
      ? true
      : !isContextSaleLoading && !isContextSaleFetching && Boolean(contextSale);

    if (
      [
        !doNotFetchMergeTagsDataSources,
        isLastAppointmentSales,
        isLastReport,
        isCurrentReport,
        isPatientProducts,
        isNextAppointment,
        isLastAppointment,
        isFollowingAppointment,
        isContextSale
      ].every(Boolean)
    ) {
      setIsDataLoaded(true);
    }
  }, [
    doNotFetchMergeTagsDataSources,
    allowInsertMergeTagsValues,
    allowMergeTags,
    isLastAppointmentFetching,
    isLastAppointmentLoading,
    isLastAppointmentSalesFetching,
    isLastAppointmentSalesLoading,
    isLastReportFetching,
    isLastReportLoading,
    isCurrentReportFetching,
    isCurrentReportLoading,
    isNextAppointmentFetching,
    isNextAppointmentLoading,
    isFollowingAppointmentFetching,
    isFollowingAppointmentLoading,
    isPatientProductsFetching,
    isPatientProductsLoading,
    patient,
    contextSale,
    isContextSaleLoading,
    isContextSaleFetching,
    isContextSaleNotAvailable,
    patientId,
    isPatientLoading
  ]);

  const currentUser = useAppSelector(selectCurrentUser);

  const [currentUserSignatureBase64, setCurrentUserSignatureBase64] = useState<
    string | null | undefined
  >(undefined);
  const [providerSignatureBase64, setProviderSignatureBase64] = useState<
    string | null | undefined
  >(undefined);

  useEffect(() => {
    const convertSignature = async () => {
      if (currentUser?.signature) {
        const base64 = await urlToBase64(currentUser.signature);
        setCurrentUserSignatureBase64(base64);
      } else {
        setCurrentUserSignatureBase64(null);
      }
    };
    convertSignature();
  }, [currentUser?.signature]);

  useEffect(() => {
    const convertSignature = async () => {
      if (contextSale?.provider?.signature) {
        const base64 = await urlToBase64(contextSale.provider.signature);
        setProviderSignatureBase64(base64);
      } else {
        setProviderSignatureBase64(null);
      }
    };
    convertSignature();
  }, [contextSale?.provider?.signature]);

  const mergeTagsDataSourceRef = useRef<MergeTagsDataSource | undefined>();

  const saleConfigurationRef = useRef<Record<string, any> | undefined>();
  const additionalMergeTagsValuesRef = useRef<
    Partial<Record<MergeTagExtraDataKeys, any>> | undefined
  >();

  useEffect(() => {
    additionalMergeTagsValuesRef.current = additionalMergeTagsValues;
  }, [additionalMergeTagsValues]);

  useEffect(() => {
    saleConfigurationRef.current = saleConfiguration;
  }, [saleConfiguration]);

  const isReadyRef = useRef<boolean>(false);

  const prevPatientIdRef = useRef(patientId);
  if (prevPatientIdRef.current !== patientId) {
    isReadyRef.current = false;
    mergeTagsDataSourceRef.current = undefined;
    prevPatientIdRef.current = patientId;
  }

  const areSignaturesConverted =
    currentUserSignatureBase64 !== undefined &&
    (isContextSaleNotAvailable ||
      !contextSale?.provider?.signature ||
      providerSignatureBase64 !== undefined);

  useEffect(() => {
    if (isDataLoaded && areSignaturesConverted && patient) {
      const currentUserWithBase64Signature = currentUser
        ? {
            ...currentUser,
            signature: currentUserSignatureBase64 ?? currentUser?.signature
          }
        : currentUser;

      const contextSaleWithBase64Signature = contextSale?.provider?.signature
        ? {
            ...contextSale,
            provider: {
              ...contextSale.provider,
              signature:
                providerSignatureBase64 ?? contextSale.provider.signature
            }
          }
        : contextSale;

      mergeTagsDataSourceRef.current = {
        contextSale: contextSaleWithBase64Signature,
        patient,
        currentAppointment: shouldSplitAppointments ? nextAppointment : undefined,
        nextAppointment: shouldSplitAppointments ? followingAppointment : nextAppointment,
        lastAppointment,
        currentUser: currentUserWithBase64Signature,
        currentClinic,
        patientProducts,
        lastReport,
        currentReport: currentReport ?? undefined,
        lastAppointmentSale
      };
      isReadyRef.current = true;
      setIsReady(true);
    }
    // Intentionally excludes stable-after-mount deps: nextAppointment, lastAppointment,
    // currentClinic, patientProducts, lastAppointmentSale.
    // contextSale?.id (not full object) avoids rebuilding on every RTK Query re-render.
    // Sale-level scalars (balance, total, total_paid/adjusted/refunds, responsibility totals,
    // provider id) cover financial and provider field changes outside .items.
    // contextSaleItemsKey captures item additions, removals, and field changes within items.
    // currentReport and lastReport ARE included — fetched mid-session on cache invalidation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDataLoaded,
    patient,
    contextSale?.id,
    contextSale?.balance,
    contextSale?.total,
    contextSale?.total_paid,
    contextSale?.total_adjusted,
    contextSale?.total_refunds,
    contextSale?.patient_responsibility_total,
    contextSale?.insurer_responsibility_total,
    contextSale?.provider?.id,
    contextSaleItemsKey,
    areSignaturesConverted,
    currentUserSignatureBase64,
    providerSignatureBase64,
    currentReport,
    lastReport,
    shouldSplitAppointments
  ]);

  // For leads contexts, build a minimal data source immediately
  // so getMergeTagValue can resolve clinic/user/lead tags without a patientId.
  // Does not wait for areSignaturesConverted to avoid blocking on slow signature fetches.
  // Re-runs when currentUserSignatureBase64 changes to pick up the converted value.
  useEffect(() => {
    if (!allowLeadsContext || !allowMergeTags || !allowInsertMergeTagsValues)
      return;

    mergeTagsDataSourceRef.current = {
      currentClinic,
      currentUser: currentUser
        ? {
            ...currentUser,
            signature: currentUserSignatureBase64 ?? currentUser.signature
          }
        : currentUser
    };
    isReadyRef.current = true;
    setIsReady(true);
  }, [
    allowLeadsContext,
    allowMergeTags,
    allowInsertMergeTagsValues,
    currentClinic,
    currentUser,
    currentUserSignatureBase64
  ]);

  /*
  Since merge tags callback getMergeTagValue used on WordProcessor initialization,
  we can't use React useState variables here, that's why we are using refs
  (isReadyRef and mergeTagsDataSourceRef)
  */
  const getMergeTagValue = useCallback(
    (mergeTag: string, tagOptions?: Record<string, any>) => {
      // Replace wildcards are reserved and never resolved against data. Return
      // the literal token (no replaced-value marker) so it persists for
      // highlighting / manual fill-in regardless of merge-tag readiness.
      if (isWildcardTag(mergeTag)) {
        return mergeTag;
      }

      if (
        !allowMergeTags ||
        !allowInsertMergeTagsValues ||
        !isReadyRef.current ||
        !mergeTagsDataSourceRef.current
      ) {
        return "";
      }

      let result =
        MERGE_TAG_REPLACED_VALUE_PREFIX +
        (buildMergeTagValue({
          name: mergeTag,
          data: mergeTagsDataSourceRef.current,
          allowHtml: Boolean(allowHtml),
          id: fieldIdRef?.current,
          defaultOptions: tagOptions
            ? tagOptions
            : saleConfigurationRef.current,
          extraData: {
            ...additionalMergeTagsValuesRef.current,
            patientSignatureURL: patientSignatureURLRef?.current
          }
        }) ?? "");

      if (!allowHtml) {
        return convertHtmlToText(getHtmlLikeText(result));
      }

      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allowMergeTags,
      allowInsertMergeTagsValues,
      allowHtml,
      maxLength,
      contextSale?.id,
      fieldIdRef,
      patient
    ]
  );

  // This function is used in Settings -> Templates when allowInsertMergeTagsValuesFeature = false
  const getMergeTagNameOrEmptyValue = useCallback(
    (mergeTag: string) => {
      // Handle SELECT_AN_INVOICE
      if (
        mergeTag === `{{${PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE}}}`
      ) {
        store.dispatch(
          setDataParams({
            id: fieldIdRef?.current,
            openSelectSaleConfigurationDialog: true
          })
        );
        return undefined;
      }

      // Handle DEFINE_PARAMETERS
      if (
        mergeTag === `{{${PATIENT_SELECTED_INVOICE_OPTIONS.DEFINE_PARAMETERS}}}`
      ) {
        store.dispatch(
          setDataParams({
            id: fieldIdRef?.current,
            openSelectSaleConfigurationDialog: true
          })
        );
        return undefined;
      }

      // Handle WITH_PREDEFINED_PARAMETERS
      if (
        mergeTag ===
        `{{${PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS}}}`
      ) {
        // Only return prefix if saleConfiguration exists
        if (saleConfigurationRef.current) {
          return undefined;
        }
        // Otherwise, open dialog
        store.dispatch(
          setDataParams({
            id: fieldIdRef?.current,
            openSelectSaleConfigurationDialog: true
          })
        );
        return undefined;
      }

      // For all other merge tags, return undefined
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    isReady,
    getMergeTagValue,
    getMergeTagNameOrEmptyValue,
    contextSale,
    isContextSaleLoading: isContextSaleNotAvailable
      ? false
      : isContextSaleLoading || isContextSaleFetching,
    doNotFetchMergeTagsDataSources,
    setDoNotFetchMergeTagsDataSources,
    refetchSale
  };
}
